export const CHILD_AUTH_BRIDGE_NAMESPACE = 'di.auth.bridge.v1';

const LEGACY_TOKEN_MESSAGE_TYPES = new Set([
  'dihr:embed-auth:token',
  'dihr:embed-auth:jwt',
  'embed-auth-token',
  'auth-token',
]);
const LEGACY_TOKEN_EXPIRED_MESSAGE_TYPES = new Set([
  'dihr:embed-auth:token-expired',
  'dihr:embed-auth:expired',
  'embed-auth-expired',
  'auth-expired',
]);
const LEGACY_LOGOUT_MESSAGE_TYPES = new Set([
  'dihr:embed-auth:logout',
  'embed-auth-logout',
  'auth-logout',
]);

export type ParentBridgeMessageType = 'parent:auth-state' | 'parent:logout' | 'parent:auth-error';
export type ChildBridgeRequestReason = 'startup' | 'refresh' | 'expired' | '401';

const tryGetOrigin = (value: string) => {
  if (!value) {
    return '';
  }

  try {
    return new URL(value).origin;
  } catch {
    return '';
  }
};

const readConfiguredParentOrigins = () => {
  const rawOrigins = [
    process.env.BUN_PUBLIC_PARENT_APP_ORIGINS || '',
    typeof document !== 'undefined' ? tryGetOrigin(document.referrer) : '',
  ]
    .join(',')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  return [...new Set(rawOrigins)];
};

export const getAllowedParentOrigins = () => {
  const configuredOrigins = readConfiguredParentOrigins();

  if (configuredOrigins.length > 0) {
    return configuredOrigins;
  }

  return [];
};

/** Origin of the host app embedding this one: the referrer when it is an allowed parent. */
export const getParentAppOrigin = () => {
  const referrerOrigin = typeof document !== 'undefined' ? tryGetOrigin(document.referrer) : '';
  const allowedOrigins = getAllowedParentOrigins();

  return allowedOrigins.includes(referrerOrigin) ? referrerOrigin : (allowedOrigins[0] ?? '');
};

export const isAllowedParentOrigin = (origin: string) => {
  const allowedOrigins = getAllowedParentOrigins();

  if (allowedOrigins.length === 0) {
    return false;
  }

  return allowedOrigins.includes(origin);
};

const isBridgeRecord = (data: unknown): data is Record<string, unknown> =>
  Boolean(data) && typeof data === 'object';

const extractBridgeType = (data: unknown) => {
  if (!isBridgeRecord(data)) {
    return '';
  }

  return typeof data.type === 'string' ? data.type.trim().toLowerCase() : '';
};

const isBridgeNamespaceMessage = (data: unknown) => {
  if (!isBridgeRecord(data)) {
    return false;
  }

  return data.namespace === CHILD_AUTH_BRIDGE_NAMESPACE;
};

const extractTokenValue = (record: Record<string, unknown>): string | null => {
  const directKeys = ['token', 'jwt', 'accessToken', 'idToken', 'customJwt'];

  for (const key of directKeys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  const payload = record.payload;
  if (payload && typeof payload === 'object') {
    return extractTokenValue(payload as Record<string, unknown>);
  }

  return null;
};

const looksLikeJwt = (value: string) => value.split('.').length === 3;

export const extractParentToken = (data: unknown): string | null => {
  if (typeof data === 'string') {
    const token = data.trim();
    return looksLikeJwt(token) ? token : null;
  }

  if (!isBridgeRecord(data)) {
    return null;
  }

  if (isBridgeNamespaceMessage(data) && extractBridgeType(data) === 'parent:auth-state') {
    const payload = data.payload;

    if (isBridgeRecord(payload) && typeof payload.accessToken === 'string') {
      const token = payload.accessToken.trim();
      return looksLikeJwt(token) ? token : null;
    }
  }

  const record = data as Record<string, unknown>;
  const type = extractBridgeType(record);
  const token = extractTokenValue(record);

  if (!token) {
    return null;
  }

  if (!type) {
    return looksLikeJwt(token) ? token : null;
  }

  return LEGACY_TOKEN_MESSAGE_TYPES.has(type) ? token : null;
};

export const isParentTokenExpiredSignal = (data: unknown) =>
  LEGACY_TOKEN_EXPIRED_MESSAGE_TYPES.has(extractBridgeType(data)) ||
  (() => {
    if (!isBridgeNamespaceMessage(data) || extractBridgeType(data) !== 'parent:logout') {
      return false;
    }

    const record = data as Record<string, unknown>;
    const payload = record.payload;
    return isBridgeRecord(payload) && payload.reason === 'token-expired';
  })();

export const isParentLogoutSignal = (data: unknown) =>
  LEGACY_LOGOUT_MESSAGE_TYPES.has(extractBridgeType(data)) ||
  (isBridgeNamespaceMessage(data) && extractBridgeType(data) === 'parent:logout');

export const isParentAuthErrorSignal = (data: unknown) =>
  isBridgeNamespaceMessage(data) && extractBridgeType(data) === 'parent:auth-error';

export const getParentLogoutReason = (data: unknown) => {
  if (!isBridgeNamespaceMessage(data) || extractBridgeType(data) !== 'parent:logout') {
    return '';
  }

  const payload = (data as Record<string, unknown>).payload;
  if (!isBridgeRecord(payload) || typeof payload.reason !== 'string') {
    return '';
  }

  return payload.reason;
};

export type ParentThemeState = {
  theme: 'light' | 'dark';
  colorTheme: 'blue' | 'red';
};

export const extractParentTheme = (data: unknown): ParentThemeState | null => {
  if (!isBridgeNamespaceMessage(data)) {
    return null;
  }

  const type = extractBridgeType(data);
  if (type !== 'parent:auth-state' && type !== 'parent:logout') {
    return null;
  }

  const payload = (data as Record<string, unknown>).payload;
  if (!isBridgeRecord(payload) || !isBridgeRecord(payload.theme)) {
    return null;
  }

  const { theme, colorTheme } = payload.theme;

  if (theme !== 'light' && theme !== 'dark') {
    return null;
  }

  if (colorTheme !== 'blue' && colorTheme !== 'red') {
    return null;
  }

  return { theme, colorTheme };
};

export const getParentAuthErrorMessage = (data: unknown) => {
  if (!isParentAuthErrorSignal(data)) {
    return '';
  }

  const payload = (data as Record<string, unknown>).payload;
  if (!isBridgeRecord(payload)) {
    return '';
  }

  return typeof payload.message === 'string' ? payload.message : '';
};

const postMessageToParent = (message: Record<string, unknown>) => {
  if (typeof window === 'undefined' || window.parent === window) {
    return false;
  }

  const allowedOrigins = getAllowedParentOrigins();
  const targets = allowedOrigins.length > 0 ? allowedOrigins : ['*'];

  targets.forEach((origin) => {
    window.parent.postMessage(message, origin);
  });

  return true;
};

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `embed-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const notifyParentChildReady = (childAppId: string, requestId = createRequestId()) => {
  postMessageToParent({
    namespace: CHILD_AUTH_BRIDGE_NAMESPACE,
    type: 'child:ready',
    payload: {
      requestId,
      childAppId,
    },
  });

  return requestId;
};

export const requestTokenFromParent = (
  reason: ChildBridgeRequestReason = 'startup',
  requestId = createRequestId(),
) => {
  postMessageToParent({
    namespace: CHILD_AUTH_BRIDGE_NAMESPACE,
    type: 'child:request-token',
    payload: {
      requestId,
      reason,
    },
  });

  return requestId;
};

/** Tells the host app which page the iframe is on, so the host can highlight its menu. */
export const notifyParentChildNavigated = (path: string, requestId = createRequestId()) => {
  postMessageToParent({
    namespace: CHILD_AUTH_BRIDGE_NAMESPACE,
    type: 'child:navigated',
    payload: {
      requestId,
      path,
    },
  });

  return requestId;
};

export const notifyParentChildLogout = (reason = 'child-logout', requestId = createRequestId()) => {
  postMessageToParent({
    namespace: CHILD_AUTH_BRIDGE_NAMESPACE,
    type: 'child:logout',
    payload: {
      requestId,
      reason,
    },
  });

  return requestId;
};
